import { useState } from 'react';
import { useConsultantReports, useCouponReport } from '../../../../api/hooks/sales/useSalesReports';
import { Table } from '../../../common/table/Table';
import { Input } from '../../../common/input/Input';
import { Button } from '../../../common/button/Button';
import { TabLayout, TabItem } from '../../../common/tab-layout/TabLayout';
import './sales-panels.scss';

const today = () => new Date().toISOString().substring(0, 10);
const monthAgo = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return d.toISOString().substring(0, 10);
};

const exportCsv = (report, from, to) => {
  const headers = [
    'Consultant', 'Email', 'Commission Rate', 'Redemptions',
    'Discount Given', 'Commission Due', 'Total Paid', 'Outstanding'
  ];
  const rows = report.map((r) => [
    `${r.firstName} ${r.lastName}`,
    r.email,
    r.commissionRate != null ? `${r.commissionRate}%` : '',
    r.totalRedemptions,
    parseFloat(r.totalDiscountGiven ?? 0).toFixed(2),
    parseFloat(r.commissionDue ?? 0).toFixed(2),
    parseFloat(r.totalPaid ?? 0).toFixed(2),
    parseFloat(r.outstandingCommission ?? 0).toFixed(2)  // already negative when overpaid,
  ]);
  const csv = [headers, ...rows].map((r) => r.map((v) => `"${v}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `commission-breakdown-${from}-to-${to}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const CONSULTANT_COLUMNS = (report, from, to) => [
  {
    id: 'name',
    header: 'Consultant',
    size: 180,
    minSize: 140,
    filterFn: 'tableFilterFn',
    accessorFn: (r) => `${r.firstName} ${r.lastName}`,
    cell: ({ row: { original: r } }) => (
      <div>
        <div style={{ fontWeight: 500 }}>{r.firstName} {r.lastName}</div>
        <div style={{ fontSize: '12px', color: 'var(--text-gray)' }}>{r.email}</div>
      </div>
    )
  },
  {
    id: 'social',
    header: 'Social',
    size: 150,
    minSize: 120,
    filterFn: 'tableFilterFn',
    accessorFn: (r) => r.socialHandle ?? '',
    cell: ({ row: { original: r } }) =>
      r.socialHandle ? (
        <span className="social-tag">
          <span className="social-tag__platform">{r.socialPlatform}</span>
          <span className="social-tag__handle">{r.socialHandle}</span>
        </span>
      ) : <span style={{ color: 'var(--text-gray)' }}>-</span>
  },
  {
    id: 'commissionRate',
    accessorKey: 'commissionRate',
    header: 'Rate',
    size: 80,
    minSize: 70,
    cell: ({ row: { original: r } }) =>
      r.commissionRate != null ? `${r.commissionRate}%` : <span style={{ color: 'var(--text-gray)' }}>-</span>
  },
  {
    id: 'totalRedemptions',
    accessorKey: 'totalRedemptions',
    header: 'Redemptions',
    size: 110,
    minSize: 90
  },
  {
    id: 'totalDiscountGiven',
    accessorKey: 'totalDiscountGiven',
    header: 'Discount Given',
    size: 130,
    minSize: 100,
    cell: ({ row: { original: r } }) => `R${parseFloat(r.totalDiscountGiven ?? 0).toFixed(2)}`
  },
  {
    id: 'commissionDue',
    accessorKey: 'commissionDue',
    header: 'Commission Due',
    size: 130,
    minSize: 100,
    cell: ({ row: { original: r } }) => (
      <span style={{ color: 'var(--color-accent)', fontWeight: 500 }}>
        R{parseFloat(r.commissionDue ?? 0).toFixed(2)}
      </span>
    )
  },
  {
    id: 'totalPaid',
    accessorKey: 'totalPaid',
    header: 'Total Paid',
    size: 110,
    minSize: 90,
    cell: ({ row: { original: r } }) => `R${parseFloat(r.totalPaid ?? 0).toFixed(2)}`
  },
  {
    id: 'outstandingCommission',
    accessorKey: 'outstandingCommission',
    header: 'Outstanding',
    size: 110,
    minSize: 90,
    cell: ({ row: { original: r } }) => {
      const outstanding = parseFloat(r.outstandingCommission ?? 0);
      const label = outstanding < 0
        ? `-R${Math.abs(outstanding).toFixed(2)}`
        : `R${outstanding.toFixed(2)}`;
      return (
        <span style={{
          color: outstanding > 0 ? 'var(--status-error)' : 'var(--status-success, #22c55e)',
          fontWeight: 500
        }}>
          {label}
        </span>
      );
    }
  },
  {
    id: 'export',
    header: '',
    size: 40,
    minSize: 40,
    maxSize: 40,
    enableSorting: false,
    disableExport: true,
    cell: () => null
  }
];

const COUPON_COLUMNS = [
  {
    id: 'code',
    accessorKey: 'code',
    header: 'Code',
    size: 130,
    minSize: 100,
    filterFn: 'tableFilterFn',
    cell: ({ row: { original: r } }) => <span className="coupon-code">{r.code}</span>
  },
  {
    id: 'discountType',
    accessorKey: 'discountType',
    header: 'Type',
    size: 120,
    minSize: 100,
    cell: ({ row: { original: r } }) => (
      r.discountType === 'PERCENTAGE' ? '% Off'
        : r.discountType === 'FIXED' ? 'Fixed Amount'
        : 'Free Delivery'
    )
  },
  {
    id: 'discountValue',
    accessorKey: 'discountValue',
    header: 'Value',
    size: 100,
    minSize: 80,
    cell: ({ row: { original: r } }) => (
      r.discountType === 'FREE_DELIVERY' ? 'Free delivery'
        : r.discountType === 'PERCENTAGE' ? `${r.discountValue}%`
        : `R${r.discountValue}`
    )
  },
  {
    id: 'usageCount',
    accessorKey: 'usageCount',
    header: 'Redemptions',
    size: 110,
    minSize: 90
  },
  {
    id: 'totalDiscountGiven',
    accessorKey: 'totalDiscountGiven',
    header: 'Total Discount Given',
    size: 160,
    minSize: 130,
    cell: ({ row: { original: r } }) => `R${parseFloat(r.totalDiscountGiven ?? 0).toFixed(2)}`
  },
  {
    id: 'consultantName',
    accessorKey: 'consultantName',
    header: 'Consultant',
    size: 160,
    minSize: 120,
    filterFn: 'tableFilterFn',
    cell: ({ row: { original: r } }) =>
      r.consultantName ?? <span style={{ color: 'var(--text-gray)' }}>-</span>
  }
];

const DateRangeBar = ({ from, to, onFromChange, onToChange, children }) => (
  <div className="reports-date-bar">
    <div className="reports-date-bar__inputs">
      <Input label="From" type="date" name="from" value={from} onChange={({ value }) => onFromChange(value)} filled />
      <Input label="To" type="date" name="to" value={to} onChange={({ value }) => onToChange(value)} filled />
    </div>
    <div className="reports-date-bar__actions">{children}</div>
  </div>
);

const ConsultantReportTab = () => {
  const [from, setFrom] = useState(monthAgo());
  const [to, setTo] = useState(today());
  const { report, isLoading, isError } = useConsultantReports(from, to);

  return (
    <div className="reports-tab-content">
      <DateRangeBar from={from} to={to} onFromChange={setFrom} onToChange={setTo}>
        {report.length > 0 && (
          <Button secondary style={{ width: 'fit-content' }} onClick={() => exportCsv(report, from, to)}>
            Export CSV
          </Button>
        )}
      </DateRangeBar>
      <Table
        id="consultant-reports"
        columns={CONSULTANT_COLUMNS(report, from, to)}
        data={report}
        loading={isLoading}
        error={isError}
        action={null}
        canExport={true}
      />
    </div>
  );
};

const CouponReportTab = () => {
  const [from, setFrom] = useState(monthAgo());
  const [to, setTo] = useState(today());
  const { report, isLoading, isError } = useCouponReport(from, to);

  return (
    <div className="reports-tab-content">
      <DateRangeBar from={from} to={to} onFromChange={setFrom} onToChange={setTo} />
      <Table
        id="coupon-reports"
        columns={COUPON_COLUMNS}
        data={report}
        loading={isLoading}
        error={isError}
        action={null}
        canExport={true}
      />
    </div>
  );
};

export const SalesReportsPanel = () => {
  const [activeTab, setActiveTab] = useState(0);

  return (
    <div className="sales-panel sales-panel--reports">
      <TabLayout activeIndex={activeTab} onChange={setActiveTab}>
        <TabItem title="By Consultant">
          <ConsultantReportTab />
        </TabItem>
        <TabItem title="Coupon Usage">
          <CouponReportTab />
        </TabItem>
      </TabLayout>
    </div>
  );
};
